<script setup lang="ts">
/**
 * One announcement (accounts.Announcement, display=modal). Teleported to
 * <body> like FeedbackModal — the toolbar's backdrop-filter would clip a fixed
 * overlay. Body is PLAIN text (pre-line), never v-html.
 */
import type { AnnouncementItem } from '~/utils/announcements'

const props = defineProps<{ item: AnnouncementItem; remaining?: number }>()
const emit = defineEmits<{ (e: 'dismiss'): void; (e: 'cta'): void }>()

const icon = computed(() => (props.item.kind === 'success' ? '✓' : props.item.kind === 'warning' ? '!' : 'i'))

function onKey(e: KeyboardEvent) {
  if (e.key === 'Escape') emit('dismiss')
}
onMounted(() => window.addEventListener('keydown', onKey))
onBeforeUnmount(() => window.removeEventListener('keydown', onKey))
</script>

<template>
  <Teleport to="body">
    <div class="anm-overlay" role="dialog" aria-modal="true" aria-labelledby="anm-title" @click.self="emit('dismiss')">
      <div class="anm-card" :class="`anm-card--${item.kind}`">
        <button type="button" class="anm-x" aria-label="Zavřít" @click="emit('dismiss')">×</button>
        <div class="anm-head">
          <span class="anm-icon" aria-hidden="true">{{ icon }}</span>
          <h2 id="anm-title" class="wb-display anm-h">{{ item.title }}</h2>
        </div>
        <p v-if="item.body" class="anm-body">{{ item.body }}</p>
        <div class="anm-actions">
          <button v-if="item.cta_url" type="button" class="wb-btn accent" @click="emit('cta')">
            {{ item.cta_label || 'Otevřít' }}
          </button>
          <button type="button" :class="item.cta_url ? 'anm-later' : 'wb-btn accent'" @click="emit('dismiss')">
            {{ item.cta_url ? 'Zavřít' : 'Rozumím' }}
          </button>
          <span v-if="remaining" class="anm-more">a ještě {{ remaining }} další</span>
        </div>
      </div>
    </div>
  </Teleport>
</template>

<style scoped>
.anm-overlay {
  position: fixed;
  inset: 0;
  z-index: 990;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 16px;
  background: color-mix(in srgb, var(--ink) 40%, transparent);
}
.anm-card {
  position: relative;
  width: 100%;
  max-width: 480px;
  max-height: calc(100dvh - 32px);
  overflow: auto;
  background: var(--paper);
  border-radius: var(--r-xl);
  padding: 26px 28px 24px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.2);
  border-top: 5px solid #2b3a8f;
}
.anm-card--success {
  border-top-color: #2f7d4f;
}
.anm-card--warning {
  border-top-color: #c08a1a;
}
.anm-x {
  position: absolute;
  top: 10px;
  right: 12px;
  border: none;
  background: transparent;
  font-size: 22px;
  line-height: 1;
  color: var(--ink-3);
  cursor: pointer;
  padding: 4px 8px;
}
.anm-head {
  display: flex;
  align-items: center;
  gap: 12px;
  margin: 0 24px 10px 0;
}
.anm-icon {
  flex: none;
  width: 30px;
  height: 30px;
  border-radius: 999px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-weight: 700;
  font-size: 15px;
  color: #fff;
  background: #2b3a8f;
}
.anm-card--success .anm-icon {
  background: #2f7d4f;
}
.anm-card--warning .anm-icon {
  background: #c08a1a;
}
.anm-h {
  font-size: 24px;
  margin: 0;
  line-height: 1.2;
}
.anm-body {
  margin: 0 0 20px;
  font-size: 15px;
  line-height: 1.55;
  color: var(--ink-2);
  white-space: pre-line;
  overflow-wrap: anywhere;
}
.anm-actions {
  display: flex;
  flex-wrap: wrap;
  align-items: center;
  gap: 12px;
}
.anm-later {
  font: inherit;
  font-size: 14px;
  background: none;
  border: none;
  color: var(--ink-3);
  cursor: pointer;
  text-decoration: underline;
}
.anm-more {
  margin-left: auto;
  font-size: 12.5px;
  color: var(--ink-3);
}
</style>
